import ReactGA from 'react-ga4';
import { routesPath } from '../common/constants/routesPath';

// every in-app route with a parameter ('/shared-report/:sharedLink', '/auth/restore-password/:token', …) as segments;
// a parameter can be a secret (reset or invitation token, share link) or an id, so none reaches Google
const PARAM_ROUTES = [
    ...Object.values(routesPath.ROOT),
    ...Object.values(routesPath.PUBLIC),
    ...Object.values(routesPath.AUTH).map(route =>
        route === routesPath.AUTH.PATH ? route : `${routesPath.AUTH.PATH}/${route}`
    ),
]
    .filter(route => route.includes('/:'))
    .map(route => route.split('/'));

// '/auth/restore-password/abc' → '/auth/restore-password/:token'; paths with no parameter stay as they are
export const maskAnalyticsPath = pathname => {
    const parts = pathname.replace(/\/+$/, '').split('/');
    const route = PARAM_ROUTES.find(
        segments =>
            segments.length === parts.length &&
            segments.every((segment, i) => segment.startsWith(':') || segment === parts[i])
    );

    return route ? route.join('/') : pathname;
};

// the page address GA records: masked path, no query (unsubscribe token, OAuth code) and no hash
export const analyticsPageLocation = (pathname, origin = window.location.origin) =>
    `${origin}${maskAnalyticsPath(pathname)}`;

// the referrer GA records. A full reload from one of our own pages (e.g. the sign-in redirect after a failed
// token refresh on a reset, invitation or share link) gives the full previous URL, so it is masked like
// page_location; another site's referrer keeps only its origin
export const analyticsReferrer = (referrer = document.referrer, origin = window.location.origin) => {
    if (!referrer) {
        return undefined;
    }

    try {
        const url = new URL(referrer);
        return url.origin === origin ? analyticsPageLocation(url.pathname, origin) : `${url.origin}/`;
    } catch (e) {
        return undefined;
    }
};

// options for ReactGA.initialize: GA's own first page view would carry the real URL, so it is off, and every
// hit of this page load carries the masked address and referrer from the start
export const analyticsInitOptions = pathname => ({
    gtagOptions: {
        send_page_view: false,
        page_location: analyticsPageLocation(pathname),
        page_referrer: analyticsReferrer(),
    },
});

// called on every navigation, before any event or page view of the new page is sent
export const setAnalyticsPage = pathname => {
    ReactGA.set({ page_location: analyticsPageLocation(pathname) });
};

// the consumer's id, never the email; null on sign-out
export const setAnalyticsUser = userId => {
    ReactGA.set({ user_id: userId ?? null });
};
